import React, { useEffect, useMemo, useRef } from "react";
import DOMPurify from "dompurify";

const PURIFY_CONFIG = {
  USE_PROFILES: { html: true },
  ADD_ATTR: ["target", "rel", "class", "style", "alt", "dir", "crossorigin"],
  FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "button"],
  FORBID_ATTR: [
    "onerror",
    "onload",
    "onclick",
    "onmouseover",
    "onfocus",
    "onblur",
    "onchange",
    "onsubmit",
  ],
  ALLOW_DATA_ATTR: false,
};

/**
 * Renders backend HTML after DOMPurify sanitization.
 * Never use raw unsanitized HTML from shared/public endpoints.
 * Broken cover images are hidden after load error (without inline JS handlers).
 */
const SafeHtml = ({ html, className, dir }) => {
  const rootRef = useRef(null);

  const sanitized = useMemo(() => {
    if (html == null || html === "") return "";
    return DOMPurify.sanitize(String(html), PURIFY_CONFIG);
  }, [html]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const hideBroken = (event) => {
      const img = event.target;
      if (!(img instanceof HTMLImageElement)) return;
      img.style.display = "none";
      const parent = img.parentElement;
      if (parent && parent.tagName === "A" && parent.children.length === 1) {
        parent.style.display = "none";
      }
    };

    const images = root.querySelectorAll("img");
    images.forEach((img) => img.addEventListener("error", hideBroken));

    return () => {
      images.forEach((img) => img.removeEventListener("error", hideBroken));
    };
  }, [sanitized]);

  if (!sanitized) return null;

  return (
    <div
      ref={rootRef}
      className={className}
      dir={dir}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

export default SafeHtml;
